import React from 'react';
import { Row } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { partial, lt, isNil } from 'ramda';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Adult from './AdultV2';
import messages from './messages';
import { getStateMappers } from './EditHouseholdStates';
import { getContactIdFromAdult } from './utils';
import { filterApplicantTypes } from '../../utils/applicantTypeUtils';

import type { FieldArrayProps } from 'redux-form';

export const formModes = {
  PROSPECT_CONVERT: 'PROSPECT_CONVERT',
  PROSPECT_HOUSEHOLD: 'PROSPECT_HOUSEHOLD',
  APPLICATION_HOUSEHOLD: 'APPLICATION_HOUSEHOLD',
  DEFAULT_MODE: 'DEFAULT_MODE',
};

type Props = {
  fields: FieldArrayProps,
  onAddNew: Function,
  onDelete: Function,
  onPrimaryChange: Function,
  dispatch: Function,
  suffixList: Array<Object>,
  relationshipList: Array<Object>,
  affordableRelationship: Array<Object>,
  applicantTypeList: Array<Object>,
  maxOccupants: number,
  fieldsRequired: boolean,
  fieldsReadOnly: boolean,
  showEditButton: boolean,
  onEdit: Function,
  isAffordable: boolean,
  primaryApplicantId: string,
  mode: string,
  leaseLocked: boolean,
  prevPrimaryId: string,
  initialValues: Object,
  guarantorOptions: Object,
  liveInCaretakerOptions: Object,
  change: Function,
  intl: Object,
};

const AdultList = ({
  fields,
  suffixList,
  relationshipList,
  affordableRelationship,
  applicantTypeList,
  onAddNew,
  onDelete,
  maxOccupants,
  fieldsRequired,
  fieldsReadOnly,
  showEditButton,
  isAffordable,
  showAffordableRelation = true,
  onPrimaryChange,
  onEdit,
  primaryApplicantId,
  mode,
  leaseLocked,
  prevPrimaryId,
  initialValues,
  dispatch,
  guarantorOptions,
  liveInCaretakerOptions,
  change,
  intl,
}: Props) => {
  const showAddButton = lt(fields.length, maxOccupants);
  const isProspectMode = mode === formModes.PROSPECT_HOUSEHOLD;
  const isEditMode = mode === formModes.APPLICATION_HOUSEHOLD;
  const isCreateApplicationMode = mode === formModes.PROSPECT_CONVERT;
  const affordableRelationshipStateManager =
    getStateMappers(formModes)[mode] ??
    getStateMappers(formModes)[formModes.DEFAULT_MODE];

  const { agingUpMinors } = useFlags();

  return (
    <div className="applicant-grp">
      <Row className="block-heading">
        <h2>
          <FormattedMessage {...messages.adultsTitle} />
        </h2>
      </Row>
      {fields.map((member, index) => {
        const memberId = fields.get(index).id;
        const isPreviousPrimary = memberId === prevPrimaryId;
        const isPrimaryApplicant = memberId === primaryApplicantId;
        const currentApplicantTypes = filterApplicantTypes({
          applicantTypesOptions: applicantTypeList,
          agingUpMinors,
          isPrimary: isPrimaryApplicant,
        });
        const processedRelationshipList =
          affordableRelationshipStateManager.getProcessedRelationshipList({
            relationshipList,
            affordableRelationship: fields.get(index).affordableRelationship,
            type: fields.get(index).type,
          });
        const affordableRelationshipBundle =
          affordableRelationshipStateManager.getAffordableRelationshipBundle({
            affordableRelationship,
            leaseLocked,
            isPreviousPrimary,
            isPrimaryApplicant,
            index,
            type:
              applicantTypeList.find((a) => a.value === fields.get(index).type)
                ?.text || '',
          });
        const currentHOHIds = fields.getAll().reduce((acc, curr, idx) => {
          const selectedRelationText = affordableRelationship
            .find((r) => r.value === curr.affordableRelationship)
            ?.text?.toLowerCase();
          if (selectedRelationText?.includes('head') && curr?.id) {
            acc.push(curr.id);
          }
          return acc;
        }, []);

        return (
          <Adult
            id={fields.get(index).id}
            field={member}
            isProspectMode={isProspectMode}
            isProspect={fields.get(index).isProspect}
            type={fields.get(index).type}
            isNew={isNil(fields.get(index).id)}
            primaryApplicantId={primaryApplicantId}
            key={`${member}${index}`}
            index={index}
            onDelete={partial(onDelete, [index])}
            showEditButton={showEditButton}
            onEdit={partial(onEdit, [fields.get(index).id])}
            suffixList={suffixList}
            relationshipList={processedRelationshipList}
            applicantTypeList={currentApplicantTypes}
            showApplicantType={fieldsRequired}
            fieldsReadOnly={fieldsReadOnly}
            isAffordable={isAffordable}
            onPrimaryChange={onPrimaryChange}
            isCreateApplicationMode={isCreateApplicationMode}
            leaseLocked={leaseLocked}
            prevPrimaryId={prevPrimaryId}
            adultInitialValues={initialValues.adults[index] || {}}
            affordableRelationshipBundle={affordableRelationshipBundle}
            guarantorOptions={guarantorOptions}
            liveInCaretakerOptions={liveInCaretakerOptions}
            change={change}
            currentHOHIndexes={currentHOHIds}
            isEditMode={isEditMode}
            intl={intl}
            showAffordableRelation={showAffordableRelation}
            contactId={getContactIdFromAdult(fields.get(index))}
          />
        );
      })}
      {showAddButton && (
        <Row>
          <a className="btn-text" onClick={onAddNew} disabled={leaseLocked}>
            <FormattedMessage {...messages.addAdult} />
          </a>
        </Row>
      )}
    </div>
  );
};

export default AdultList;
